import { Link } from "react-router-dom";
import logo from "@/assets/logo.webp";
import { Mail, Phone, Instagram, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NewsletterSignup } from "@/components/NewsletterSignup";


const Footer = () => {
  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img src={logo} alt="MSB Camps" width="128" height="128" loading="lazy" className="h-16 w-auto mb-4" />
            <p className="text-primary-foreground/80 mb-6">
              Inspiring children to play, experience, and grow through enriching programs and unforgettable adventures.
            </p>
            <div className="flex gap-3">
              <SocialButton 
                icon={<Instagram className="w-5 h-5" />} 
                href="https://www.instagram.com/msbcampsaz/"
                label="Follow MSB Camps on Instagram"
              />
              <SocialButton 
                icon={<Facebook className="w-5 h-5" />} 
                href="https://www.facebook.com/profile.php?id=61578235777899"
                label="Follow MSB Camps on Facebook"
              />
            </div>
          </div>

          {/* Programs */}
          <div>
            <h2 className="font-bold text-lg mb-5">Programs</h2>
            <ul className="space-y-3">
              <FooterLink to="/after-school">After School</FooterLink>
              <FooterLink to="/early-care">Early Care</FooterLink>

              <FooterLink to="/clubs">Clubs</FooterLink>
              <FooterLink to="/holiday-camps">Holiday Break Camps</FooterLink>
              <FooterLink to="/summer-camps">Summer Camps</FooterLink>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h2 className="font-bold text-lg mb-5">Company</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/dashboard" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">
                  Parent Portal
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h2 className="font-bold text-lg mb-5">Contact Us</h2>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-secondary shrink-0" />
                <span className="text-primary-foreground/80">(602) 550-0931</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-secondary shrink-0" />
                <span className="text-primary-foreground/80">info@msbcamps.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        <div className="mt-12 pt-8 border-t border-primary-foreground/10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <h2 className="font-bold text-lg mb-1">Join our newsletter</h2>
            <p className="text-primary-foreground/80 text-sm">
              Program updates, camp announcements, and early registration — straight to your inbox.
            </p>
          </div>
          <NewsletterSignup source="footer" variant="footer" />
        </div>
      </div>


      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container mx-auto px-4 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-primary-foreground/90 text-sm">
            © 2025 MSB Camps. All rights reserved.
          </p>
          <Link to="/policies" className="text-primary-foreground/90 hover:text-primary-foreground transition-colors text-sm">
            Privacy Policy & Terms of Service
          </Link>
        </div>
      </div>
    </footer>
  );
};

const FooterLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <li>
    <Link to={to} className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">
      {children}
    </Link>
  </li>
);

const SocialButton = ({ icon, href, label }: { icon: React.ReactNode; href: string; label: string }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
    <Button
      variant="ghost"
      size="icon"
      aria-label={label}
      className="rounded-full bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground"
    >
      {icon}
    </Button>
  </a>
);

export default Footer;